import { Field, Label } from '@headlessui/react';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { CloseIcon } from '../../../../assets/icons/index';
import MinusIcon from '../../../../assets/icons/minus-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Checkbox } from '../../../../components/checkbox/index';
import { Table } from '../../../../components/table/index';
import { QueryKey } from '../../../../constants/index';
import { SharePlan, useInvalidateQueries } from '../../../../hooks/index';
import { TargetBasedVestingHookMode } from '../../../../hooks/share-plan/index';
import { selectedCompanyStorage } from '../../../../storage/company/selected-company.util';
import { ApproveMilestoneDto } from '../../../../types/vesting-tasks.types';
import { calcMilestoneIssuedShares } from '../../../../utils/calc-milestone-issued-shares.util';
import {
  ApproveTargetBasedColumn,
  approveTargetBasedColumnMap,
  approveTargetBasedColumnTitle,
} from './approve-target.types';
import { TableRow } from './table-row';

export type ApproveTargetModalProps = {
  onClose: () => void;
};

type ApproveTarget = {
  milestone: { name: string; targetDate: Date; weight: number };
} & ApproveMilestoneDto;

const generateMilestoneId = ({
  id,
  milestone: { name, targetDate, weight },
}: {
  id: string;
  milestone: { name: string; targetDate: Date; weight: number };
}) => `${id}-${name}-${targetDate}-${weight}`;

export const ApproveTargetModal: FC<ApproveTargetModalProps> = ({ onClose }) => {
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_UNREAD_NOTIFICATION_COUNT,
    QueryKey.APPROVE_TARGET_BASED_MILESTONE,
  );
  const [selectedVestingTasks, setSelectedVestingTasks] = useState<ApproveTarget[]>([]);

  const { targetBasedVesting = [] } =
    SharePlan.useTargetBasedVesting<TargetBasedVestingHookMode.GET_DATA>({
      mode: TargetBasedVestingHookMode.GET_DATA,
    });

  const onDeselectAll = () => setSelectedVestingTasks([]);

  const onSelectAll = (data: ApproveTarget[]) => {
    setSelectedVestingTasks(data);
  };

  const { approve, isPending } = SharePlan.useApproveTargetBasedVestingTasks({
    companyId,
  });

  const milestones = targetBasedVesting.flatMap(
    ({
      finished,
      grantItem: { id: grantId, plan, stakeholder },
      id,
      targetBasedVestingMilestones: milestones = [],
      totalSharesCount,
    }) => {
      if (finished) return [];
      return milestones
        .map((milestone, milestoneIndex) => {
          if (milestone.isCompleted || new Date(milestone.targetDate).getTime() >= Date.now())
            return null;

          return {
            milestone,
            milestoneId: generateMilestoneId({
              id,
              milestone,
            }),
            milestoneIndex: milestoneIndex,
            milestones,
            grantId,
            plan,
            stakeholder,
            totalSharesCount,
            vestingTaskId: id,
          };
        })
        .filter((m) => m !== null);
    },
  );

  return (
    <div className="flex flex-col gap-8 overflow-x-auto rounded-2xl bg-white p-8">
      <div className="flex w-full justify-between">
        <span className="text-xl font-semibold text-gray-700">Target Based Vesting Approval</span>
        <Button
          className="centered flex size-7 rounded-full border border-gray-100 shadow-sm"
          onClick={onClose}
          styleType="NONE"
        >
          <CloseIcon />
        </Button>
      </div>

      <div className="max-h-[400px] overflow-y-auto bg-white min-[1650px]:overflow-x-hidden">
        <Table
          columnOrder={approveTargetBasedColumnMap}
          columnsTitle={{
            ...approveTargetBasedColumnTitle,
            [ApproveTargetBasedColumn.SELECT]: (
              <Field className="flex cursor-pointer items-center">
                <Checkbox
                  checked={selectedVestingTasks.length > 0}
                  id="select-all-hurdle-plans"
                  onChange={(checked) =>
                    checked
                      ? onSelectAll(
                          milestones.map(
                            ({
                              milestone,
                              milestoneIndex,
                              milestones: taskMilestones,
                              totalSharesCount,
                              vestingTaskId,
                            }) => ({
                              milestone,
                              milestoneIndex,
                              value: calcMilestoneIssuedShares({
                                milestoneIndex,
                                milestones: taskMilestones,
                                totalSharesCount,
                              }),
                              vestingTaskId,
                            }),
                          ),
                        )
                      : onDeselectAll()
                  }
                  selectIcon={<MinusIcon />}
                />
                <Label
                  className="absolute left-0 top-0 h-full w-full cursor-pointer"
                  htmlFor="select-all-hurdle-plans"
                />
              </Field>
            ),
          }}
          tHeadClassNames={{ thClassName: 'pl-4' }}
        >
          {milestones.map(
            ({
              milestone,
              milestoneId,
              milestoneIndex,
              milestones,
              grantId,
              plan,
              stakeholder,
              totalSharesCount,
              vestingTaskId,
            }) => {
              return (
                <TableRow
                  grantId={grantId}
                  id={milestoneId}
                  isSelected={selectedVestingTasks.some(
                    ({ milestone, vestingTaskId }) =>
                      generateMilestoneId({
                        id: vestingTaskId,
                        milestone,
                      }) === milestoneId,
                  )}
                  key={milestoneId}
                  milestone={milestone}
                  milestoneIndex={milestoneIndex}
                  milestones={milestones}
                  onRemoveSelect={() =>
                    setSelectedVestingTasks((prev) =>
                      prev.filter(
                        () =>
                          generateMilestoneId({
                            id: vestingTaskId,
                            milestone,
                          }) !== milestoneId,
                      ),
                    )
                  }
                  onSelect={({ value }) =>
                    setSelectedVestingTasks((prev) => [
                      ...prev,
                      {
                        milestone,
                        milestoneIndex,
                        value,
                        vestingTaskId,
                      },
                    ])
                  }
                  planName={plan.name}
                  planType={plan.type}
                  shareClassName={plan.pool?.shareClass?.name ?? ''}
                  stakeholderName={stakeholder.fullName}
                  totalSharesCount={totalSharesCount}
                  totalVested={milestones.reduce(
                    (acc, { isCompleted, weight }) => (acc += isCompleted ? weight : 0),
                    0,
                  )}
                />
              );
            },
          )}
        </Table>
      </div>
      <div className="flex h-11 w-full justify-between overflow-hidden text-sm">
        <Button className="w-fit font-[450] text-gray-700" styleType="NONE">
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <Button
          className="w-[260px] font-[550] text-white"
          disabled={selectedVestingTasks.length === 0}
          isLoading={isPending}
          onClick={() => {
            approve(selectedVestingTasks, {
              onSuccess: () => {
                toast.success(<AppFormattedMessage id="EXERCISED_SUCCESSFULLY" />);
                onClose();
                invalidateQuery();
              },
            });
            setSelectedVestingTasks([]);
          }}
        >
          Approve Targets
        </Button>
      </div>
    </div>
  );
};
